import {
  cloneLearningContentWorksheetDocument,
  parseLearningContentWorksheetDocument,
  type LearningContentWorksheetDocumentV1,
} from '../types/learningContentWorksheetCanvas';
import type { ProgramWorksheetLearningTextPatch } from '../services/programContentWorksheetService';
import {
  fetchProgramContentWorksheet,
  learningTextWithWorksheetRef,
  resolveOrCreateAnnualWorksheetLearningPatch,
  upsertProgramContentWorksheet,
} from '../services/programContentWorksheetService';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import {
  notifyProgramContentSlideLearningUpdated,
  type ProgramContentSlidePayloadV1,
} from '../utils/programContentSlideStorage';
import { finalizePendingLearningWorksheetImagesInLayout } from './learningWorksheetImagePendingUpload';
import { mergeLearningTextPatchWithCurrentDbBaseline } from './programLearningTextPatchMerge';
import {
  buildLearningContentWorksheetDraftStorageKey,
  clearLearningContentWorksheetDraft,
} from './learningContentWorksheetCanvasDraftStorage';
import {
  getWorksheetDocumentPages,
  sheetLayoutSubsetToSlideLayout,
  slideLayoutToSheetLayoutSubset,
} from '../types/learningContentWorksheetCanvas';

export const LEARNING_WORKSHEET_PENDING_PREFIX = 'cp:learning-worksheet-pending:v1:';
export const LEARNING_WORKSHEET_PENDING_CHANGED_EVENT = 'cp:learning-worksheet-pending-changed';

export interface LearningWorksheetPendingRecordV1 {
  v: 1;
  workspaceId: string;
  scopeId: string;
  worksheetRefId: string;
  annualKey: string;
  document: LearningContentWorksheetDocumentV1;
  learningPatch?: ProgramWorksheetLearningTextPatch | null;
  annualFlushMeta?: {
    academicYear: number;
    termNumber: 1 | 2;
    gradeId: string;
    nextLearningText: string;
    slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
    slideWorkspaceScopeId: string;
    createProgramRowContext?: ProgramContentSlidePayloadV1['createPresentationRefOnSave'];
  } | null;
  updatedAt: number;
}

function draftStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function pendingKey(workspaceId: string, worksheetRefId: string): string {
  return `${LEARNING_WORKSHEET_PENDING_PREFIX}${workspaceId.trim()}:${worksheetRefId.trim()}`;
}

function notifyPendingChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function readPendingRecord(
  workspaceId: string,
  worksheetRefId: string
): LearningWorksheetPendingRecordV1 | null {
  const sid = workspaceId.trim();
  const refId = worksheetRefId.trim();
  if (!sid || !refId) return null;
  const storage = draftStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(pendingKey(sid, refId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LearningWorksheetPendingRecordV1;
    if (!parsed || parsed.v !== 1) return null;
    if ((parsed.workspaceId ?? '').trim() !== sid) return null;
    if ((parsed.worksheetRefId ?? '').trim() !== refId) return null;
    const doc = parseLearningContentWorksheetDocument(parsed.document);
    if (!doc) return null;
    return { ...parsed, document: doc };
  } catch {
    return null;
  }
}

function writePendingRecord(rec: LearningWorksheetPendingRecordV1): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(
      pendingKey(rec.workspaceId, rec.worksheetRefId),
      JSON.stringify({
        ...rec,
        document: cloneLearningContentWorksheetDocument(rec.document),
      })
    );
    notifyPendingChanged();
  } catch {
    /* ignore */
  }
}

function removePendingRecord(workspaceId: string, worksheetRefId: string): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.removeItem(pendingKey(workspaceId, worksheetRefId));
    notifyPendingChanged();
  } catch {
    /* ignore */
  }
}

function listPendingRecords(scopeIds?: readonly string[]): LearningWorksheetPendingRecordV1[] {
  const storage = draftStorage();
  if (!storage) return [];
  const scopeSet = scopeIds && scopeIds.length ? new Set(scopeIds.map((s) => s.trim()).filter(Boolean)) : null;
  const out: LearningWorksheetPendingRecordV1[] = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith(LEARNING_WORKSHEET_PENDING_PREFIX)) continue;
      const raw = storage.getItem(key);
      if (!raw) continue;
      const rec = JSON.parse(raw) as LearningWorksheetPendingRecordV1;
      if (!rec || rec.v !== 1) continue;
      const scopeId = (rec.scopeId ?? '').trim();
      if (scopeSet && !scopeSet.has(scopeId)) continue;
      const doc = parseLearningContentWorksheetDocument(rec.document);
      if (!doc) continue;
      out.push({ ...rec, document: doc });
    }
  } catch {
    return out;
  }
  return out;
}

export function queueLearningWorksheetPendingFlush(params: {
  workspaceId: string;
  scopeId: string;
  worksheetRefId: string;
  annualKey: string;
  document: LearningContentWorksheetDocumentV1;
  learningPatch?: ProgramWorksheetLearningTextPatch | null;
  annualFlushMeta?: LearningWorksheetPendingRecordV1['annualFlushMeta'];
}): void {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  const refId = params.worksheetRefId.trim();
  const annualKey = params.annualKey.trim();
  if (!sid || !scopeId || !refId || !annualKey) return;
  writePendingRecord({
    v: 1,
    workspaceId: sid,
    scopeId,
    worksheetRefId: refId,
    annualKey,
    document: cloneLearningContentWorksheetDocument(params.document),
    learningPatch: params.learningPatch ?? null,
    annualFlushMeta: params.annualFlushMeta ?? null,
    updatedAt: Date.now(),
  });
}

export function hasPendingLearningWorksheetFlush(workspaceId: string, worksheetRefId: string): boolean {
  return !!readPendingRecord(workspaceId, worksheetRefId);
}

export function hasPendingLearningWorksheetFlushForAnnualKey(workspaceId: string, annualKey: string): boolean {
  return hasPendingLearningWorksheetFlushForAnnualKeys(workspaceId, annualKey);
}

export function resolvePendingWorksheetRefIdForAnnualKey(workspaceId: string, annualKey: string): string {
  return resolvePendingWorksheetRefIdForAnnualKeys(workspaceId, annualKey);
}

/** `annualKey`·`draftRowKey` 등 별칭 키로 pending 활동지 ref 조회 */
export function resolvePendingWorksheetRefIdForAnnualKeys(
  workspaceId: string,
  ...annualKeys: string[]
): string {
  const sid = workspaceId.trim();
  const keySet = new Set(annualKeys.map((k) => k.trim()).filter(Boolean));
  if (!sid || !keySet.size) return '';
  const rec = listPendingRecords().find(
    (row) => row.workspaceId.trim() === sid && keySet.has(row.annualKey.trim())
  );
  return (rec?.worksheetRefId ?? '').trim();
}

export function hasPendingLearningWorksheetFlushForAnnualKeys(
  workspaceId: string,
  ...annualKeys: string[]
): boolean {
  const sid = workspaceId.trim();
  const keySet = new Set(annualKeys.map((k) => k.trim()).filter(Boolean));
  if (!sid || !keySet.size) return false;
  return listPendingRecords().some(
    (row) => row.workspaceId.trim() === sid && keySet.has(row.annualKey.trim())
  );
}

export function hasPendingLearningWorksheetDraftsForScope(scopeId: string): boolean {
  const sid = scopeId.trim();
  if (!sid) return false;
  return listPendingRecords([sid]).length > 0;
}

export function hasAnyPendingLearningWorksheetDrafts(): boolean {
  return listPendingRecords().length > 0;
}

/** 프로그램 통합 저장(학년·학기 범위) — pending 활동지 scopeId 수집용 */
export function collectPendingLearningWorksheetScopeIds(): string[] {
  const out = new Set<string>();
  listPendingRecords().forEach((rec) => {
    const scopeId = (rec.scopeId ?? '').trim();
    if (scopeId) out.add(scopeId);
  });
  return Array.from(out);
}

export function loadLearningWorksheetPendingDocument(
  workspaceId: string,
  worksheetRefId: string
): LearningContentWorksheetDocumentV1 | null {
  return readPendingRecord(workspaceId, worksheetRefId)?.document ?? null;
}

export function discardPendingLearningWorksheetFlush(workspaceId: string, worksheetRefId: string): void {
  const sid = workspaceId.trim();
  const refId = worksheetRefId.trim();
  if (!sid || !refId) return;
  removePendingRecord(sid, refId);
}

export function discardPendingLearningWorksheetFlushForAnnualKey(workspaceId: string, annualKey: string): void {
  const sid = workspaceId.trim();
  const key = annualKey.trim();
  if (!sid || !key) return;
  const rec = listPendingRecords().find(
    (row) => row.workspaceId.trim() === sid && row.annualKey.trim() === key
  );
  if (rec) removePendingRecord(sid, rec.worksheetRefId);
}

/** 저장하지 않고 이탈 시 — 지정 scope(또는 전체) 활동지 pending 초안을 제거한다. */
export function discardPendingLearningWorksheetDraftsForScopes(scopeIds?: readonly string[]): void {
  const records = listPendingRecords(scopeIds);
  if (!records.length) return;
  records.forEach((rec) => {
    const sid = (rec.workspaceId ?? '').trim();
    const refId = (rec.worksheetRefId ?? '').trim();
    if (!sid || !refId) return;
    removePendingRecord(sid, refId);
  });
}

export async function resolveWorksheetDocumentForEditor(params: {
  workspaceId: string;
  worksheetRefId: string | null;
  draftStorageKey: string;
}): Promise<LearningContentWorksheetDocumentV1 | null> {
  const refId = (params.worksheetRefId ?? '').trim();
  if (refId) {
    const pending = loadLearningWorksheetPendingDocument(params.workspaceId, refId);
    if (pending) return pending;
  }
  /**
   * 로컬 편집 초안(`loadLearningContentWorksheetDraft`)은 여기서 고르지 않는다.
   * (자동 저장은 없어졌고, 예전 버전이 남긴 초안은 편집 모달이 한 번 불러온 뒤 지운다)
   */
  if (!refId) return null;
  return fetchProgramContentWorksheet(refId);
}

async function flushPendingRecord(rec: LearningWorksheetPendingRecordV1): Promise<{
  ok: boolean;
  owner: { kind: 'program_cell' | 'fixed_row' | 'scoped_row'; id: string } | null;
  needsFullUiRefresh: boolean;
}> {
  const sid = rec.workspaceId.trim();
  const refId = rec.worksheetRefId.trim();
  if (!sid || !refId) return { ok: false, owner: null, needsFullUiRefresh: false };

  const pages = getWorksheetDocumentPages(rec.document);
  const previousLayouts = await fetchProgramContentWorksheet(refId).then((doc) =>
    doc ? getWorksheetDocumentPages(doc).map((page) => sheetLayoutSubsetToSlideLayout(page)) : []
  );

  const finalizedPages = [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const layout = sheetLayoutSubsetToSlideLayout(pages[pageIndex]);
    const finalizedLayout = await finalizePendingLearningWorksheetImagesInLayout({
      layout,
      workspaceId: sid,
      previousLayout: previousLayouts[pageIndex] ?? null,
    });
    finalizedPages.push(slideLayoutToSheetLayoutSubset(finalizedLayout));
  }

  const document = cloneLearningContentWorksheetDocument({
    ...rec.document,
    pages: finalizedPages,
    layout: finalizedPages[0],
  });

  let learningPatch = rec.learningPatch ?? null;
  if (!learningPatch && rec.annualFlushMeta) {
    const meta = rec.annualFlushMeta;
    learningPatch = await resolveOrCreateAnnualWorksheetLearningPatch({
      workspaceId: sid,
      academicYear: meta.academicYear,
      termNumber: meta.termNumber,
      gradeId: meta.gradeId,
      nextLearningText: meta.nextLearningText,
      slideOpenDbOwner: meta.slideOpenDbOwner,
      slideWorkspaceScopeId: meta.slideWorkspaceScopeId,
      createProgramRowContext: meta.createProgramRowContext,
    });
  }

  if (learningPatch?.id?.trim() && learningPatch.learningText) {
    try {
      const mergedLearningText = await mergeLearningTextPatchWithCurrentDbBaseline({
        workspaceId: sid,
        kind: learningPatch.kind,
        id: learningPatch.id.trim(),
        learningText: learningPatch.learningText,
      });
      learningPatch = { ...learningPatch, learningText: mergedLearningText };
    } catch {
      /* baseline 조회 실패 시 기존 patch 그대로 시도 */
    }
  }

  await upsertProgramContentWorksheet({
    workspaceId: sid,
    worksheetRefId: refId,
    document,
    learningPatch,
  });

  /**
   * 프레젠 flush의 setPresenceCached와 동일 역할:
   * pending/초안을 지우기 전에 learning_text(ref)를 표·연간진도표에 알려
   * 통합 저장 직후 인디케이터가 비지 않게 한다.
   */
  const nextLearningText =
    (learningPatch?.learningText ?? '').trim() ||
    learningTextWithWorksheetRef(
      rec.annualFlushMeta?.nextLearningText ?? '',
      refId
    );
  if (nextLearningText.trim()) {
    const patchOwner =
      learningPatch?.id?.trim() &&
      (learningPatch.kind === 'program_cell' ||
        learningPatch.kind === 'fixed_row' ||
        learningPatch.kind === 'scoped_row')
        ? ({ kind: learningPatch.kind, id: learningPatch.id.trim() } as ProgramContentSlideOwnerRef)
        : null;
    notifyProgramContentSlideLearningUpdated({
      workspaceId: sid,
      scopeId: rec.scopeId,
      annualKey: (rec.annualKey ?? '').trim() || undefined,
      learningText: nextLearningText,
      owner: patchOwner,
      syncAnnualLearningTextOverlay: true,
    });
  }

  removePendingRecord(sid, refId);
  clearLearningContentWorksheetDraft(rec.annualKey);

  const owner =
    learningPatch?.id?.trim() &&
    (learningPatch.kind === 'program_cell' ||
      learningPatch.kind === 'fixed_row' ||
      learningPatch.kind === 'scoped_row')
      ? { kind: learningPatch.kind, id: learningPatch.id.trim() }
      : null;
  /** 표 행 learning 필드를 바꿨는데 owner를 못 알면 전량 재조회 */
  const needsFullUiRefresh = !!learningPatch && !owner;
  return { ok: true, owner, needsFullUiRefresh };
}

/** 활동지 한 건만 바로 DB에 반영 (편집 모달 저장 버튼) */
export async function flushPendingLearningWorksheetDraftForRef(
  workspaceId: string,
  worksheetRefId: string
): Promise<void> {
  const rec = readPendingRecord(workspaceId, worksheetRefId);
  if (!rec) return;
  const result = await flushPendingRecord(rec);
  if (!result.ok) throw new Error('활동지를 저장하지 못했습니다.');
}

export type LearningWorksheetFlushResult = {
  flushed: number;
  owners: Array<{ kind: 'program_cell' | 'fixed_row' | 'scoped_row'; id: string }>;
  needsFullUiRefresh: boolean;
};

export async function flushPendingLearningWorksheetDrafts(
  scopeIds?: readonly string[]
): Promise<LearningWorksheetFlushResult> {
  if (Array.isArray(scopeIds) && scopeIds.length === 0) {
    return { flushed: 0, owners: [], needsFullUiRefresh: false };
  }
  const records = listPendingRecords(scopeIds);
  if (!records.length) return { flushed: 0, owners: [], needsFullUiRefresh: false };
  let flushed = 0;
  let needsFullUiRefresh = false;
  const owners: LearningWorksheetFlushResult['owners'] = [];
  const failures: Array<{ worksheetRefId: string; annualKey: string; message: string }> = [];
  for (const rec of records) {
    try {
      const result = await flushPendingRecord(rec);
      if (result.ok) {
        flushed += 1;
        if (result.owner) owners.push(result.owner);
        if (result.needsFullUiRefresh) needsFullUiRefresh = true;
      }
    } catch (error) {
      failures.push({
        worksheetRefId: rec.worksheetRefId,
        annualKey: rec.annualKey,
        message: error instanceof Error ? error.message : 'unknown',
      });
    }
  }
  if (failures.length) {
    const first = failures[0];
    throw new Error(
      `활동지 초안 ${failures.length}건을 저장하지 못했습니다. (ref: ${first.worksheetRefId}, key: ${first.annualKey}) ${first.message}`
    );
  }
  return { flushed, owners, needsFullUiRefresh };
}
